import { Link, useLocation } from "react-router-dom";
import { useQuietToolbar } from "@/lib/quiet-toolbar";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Home } from "lucide-react";
import { api } from "@/lib/api";
import { assetCrumbLabel } from "./crumb-labels";

const LABELS: Record<string, string> = {
    inbox: "Tasks",
    "doesnt-add-up": "Doesn't add up",
    "task-queues": "Task Queues",
    jobs: "Jobs",
    customers: "Customers",
    "office-review": "Office Review",
    "close-out": "Closeout",
    commercial: "Files & Documents",
    dispatch: "The Grid",
    catalog: "Catalog",
    pricebook: "Fieldbook",
    field: "Field",
    assets: "Devices",
    sites: "Locations",
    backflow: "Backflow",
    "fire-safety": "Fire Safety",
    "work-order": "Work Order",
    "work-order-pdf": "Work Order PDF",
    supplies: "Supplies",
    profile: "Profile",
    help: "Help KB",
    payroll: "Payroll",
    inspections: "Inspections",
    compliance: "Compliance",
    // #1426 — the Compliance section's tabs and provider pages.
    licenses: "Licenses & insurance",
    due: "What is due",
    // #1429: the route keeps the code name `exposure`; the crumb says what the
    // screen says, because the office does not call it that.
    exposure: "Still owed",
    filing: "City filing",
    authorities: "Water providers",
    platforms: "Filing websites",
    jurisdictions: "Which city a location is in",
    forms: "Report forms",
    gauges: "Test gauges",
    coi: "Insurance certificates",
    tools: "Set-up tools",
    edit: "Edit",
    cockpit: "Licenses & insurance",
    attachments: "Attachments",
    automations: "Automations",
    reports: "Reports",
    settings: "Settings",
    admin: "Admin",
    users: "Users",
    new: "New",
    schedule: "Schedule",
    map: "Map",
    "route-planner": "Route Planner",
    review: "Review",
    rules: "Rules",
    profiles: "Profiles",
    run: "Run",
    timeclock: "Time Clock",
    adjustments: "Adjustments",
    points: "My Points",
    "point-board": "Point board",
    perform: "Perform",
    people: "People",
    accounting: "Accounting",
    "ar-aging": "AR Aging",
    "invoices-attention": "Invoices Needing Attention",
    "estimates-attention": "Estimates Needing Attention",
    "failed-payments": "Failed Payments",
    "old-checks": "Old checks",
    "test-due": "Test Due",
    "compliance-due": "Compliance Due",
    "photo-backlog": "Photo Backlog",
    "quote-tester": "Quote Tester",
    "repair-finder": "Find what fits",
    "bulk-edit": "Bulk Edit",
    "bulk-jurisdiction": "Bulk Jurisdiction",
    "org-settings": "Organization Settings",
    "system-health": "System Health",
    "form-templates": "Form Mapper",
    "wo-vendors": "WO Vendors",
    "supply-houses": "Supply Houses",
    "license-types": "License Types",
    "water-providers": "Water Providers",
    "reporting-platforms": "Reporting Platforms",
    "email-templates": "Email Templates",
    "job-types": "Job Types",
    "tech-points-budgets": "Tech Points Budgets",
    "auto-schedule-activity": "Auto-Scheduler Activity",
    "scheduling-report": "Scheduling report",
    "photo-import-reports": "Photo Import Reports",
    "recipient-shadow": "Recipient Shadow Checks",
    "qbo-sync-issues": "QBO Sync Issues",
    "pto-requests": "PTO Requests",
    "hr-cockpit": "HR Cockpit",
    "device-intelligence": "Device Intelligence",
};

function isUuid(s: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Resolve a friendly name for a `users/:uuid` breadcrumb segment. Reuses the
 * exact React Query key the user-detail page already populates so it dedupes
 * (no extra request when arriving from the list); falls back to the page's own
 * fetch if landed on directly. Returns { userId, name } or null.
 */
function useUserCrumbName(parts: string[]): { userId: string; name: string } | null {
    let userId: string | null = null;
    for (let i = 0; i < parts.length - 1; i += 1) {
        if (parts[i] === "users" && isUuid(parts[i + 1])) userId = parts[i + 1];
    }
    const { data } = useQuery({
        queryKey: ["user-profile", userId],
        queryFn: () =>
            api.get<{ profile: { identity: { firstName: string; lastName: string; email: string | null } } }>(
                `/api/users/${userId}/profile`,
            ),
        enabled: !!userId,
        staleTime: 60_000,
        retry: false,
    });
    if (!userId || !data?.profile) return null;
    const { firstName, lastName, email } = data.profile.identity;
    const name = `${firstName} ${lastName}`.trim() || email || `#${userId.slice(0, 8)}`;
    return { userId, name };
}

/** Last `<segment>/:uuid` pair in the path, or null. */
function findEntityId(parts: string[], segment: string): string | null {
    let id: string | null = null;
    for (let i = 0; i < parts.length - 1; i += 1) {
        if (parts[i] === segment && isUuid(parts[i + 1])) id = parts[i + 1];
    }
    return id;
}

// Field workflow routes are all /field/<segment>/:jobId. The uuid after any
// of these segments is a JOB id, so the crumb trail can resolve the job title
// and link back to /field/jobs/:id (the bare list paths have no page).
const FIELD_WORKFLOW_SEGMENTS = new Set(["jobs", "backflow", "fire-safety", "work-order", "work-order-pdf"]);

/** Job uuid in a /field/<workflow>/:uuid trail, or null. */
function findFieldJobId(parts: string[]): string | null {
    if (parts[0] !== "field" || parts.length < 3) return null;
    if (!FIELD_WORKFLOW_SEGMENTS.has(parts[1])) return null;
    return isUuid(parts[2]) ? parts[2] : null;
}

/**
 * Resolve a friendly name for a `customers/:uuid` breadcrumb segment.
 * Reuses the exact query key ["customer", id] the customer-detail page
 * populates, so it dedupes off the warm cache (no extra request when
 * arriving from the list) and falls back to its own fetch on direct load.
 */
function useCustomerCrumbName(parts: string[]): { id: string; label: string } | null {
    const id = findEntityId(parts, "customers");
    const { data } = useQuery({
        queryKey: ["customer", id],
        queryFn: () => api.get<{ customer: { company_name: string | null } }>(`/api/customers/${id}`),
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id || !data?.customer) return null;
    const label = data.customer.company_name?.trim() || `#${id.slice(0, 8)}`;
    return { id, label };
}

/**
 * #1916: a received payment's crumb reads its check number, not its id.
 * Shares the payment page's ["received-payments", id] cache entry.
 */
function usePaymentCrumbName(parts: string[]): { id: string; label: string } | null {
    const id = findEntityId(parts, "payments");
    const { data } = useQuery({
        queryKey: ["received-payments", id],
        queryFn: () => api.get<{ reference: string | null; method: string }>(`/api/received-payments/${id}`),
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id || !data) return null;
    return { id, label: data.reference ? `Check #${data.reference.replace(/^#/, "")}` : "Payment" };
}

/**
 * Resolve the invoice number for an `invoices/:uuid` breadcrumb segment.
 * Mirrors the invoice-detail page's query key ["invoice", id] AND its
 * cached shape ({ invoice, lines, payments }) so neither consumer
 * clobbers the other's cache entry.
 */
function useInvoiceCrumbName(parts: string[]): { id: string; label: string } | null {
    const id = findEntityId(parts, "invoices");
    const { data } = useQuery({
        queryKey: ["invoice", id],
        queryFn: async () => {
            const r = await api.get<{
                invoice: { invoice_number: string | null };
                lines?: unknown[];
                payments?: unknown[];
            }>(`/api/invoices/${id}`);
            return { invoice: r.invoice, lines: r.lines ?? [], payments: r.payments ?? [] };
        },
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id || !data?.invoice) return null;
    const num = data.invoice.invoice_number;
    const label = num ? `#${num}` : `#${id.slice(0, 8)}`;
    return { id, label };
}

/**
 * Resolve a friendly name for a `sites/:uuid` breadcrumb segment. Mirrors the
 * job form's query key ["site-detail", id] so it dedupes off a warm cache, and
 * falls back to its own fetch on direct load. Prefers the site nickname, then
 * the street address. Also resolves the parent customer so the trail can gain
 * the customer level (Location Page V2 item 1: Customers > {Customer} > {Site}).
 */
function useSiteCrumbName(
    parts: string[],
): { id: string; label: string; customerId: string | null; customerName: string | null } | null {
    const id = findEntityId(parts, "sites");
    const { data } = useQuery({
        queryKey: ["site-detail", id],
        queryFn: () =>
            api.get<{
                site: {
                    name: string | null;
                    address_line: string | null;
                    customer_id: string | null;
                    customer_name: string | null;
                };
            }>(`/api/sites/${id}`),
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id || !data?.site) return null;
    const label = data.site.name?.trim() || data.site.address_line?.trim() || `#${id.slice(0, 8)}`;
    return {
        id,
        label,
        customerId: data.site.customer_id ?? null,
        customerName: data.site.customer_name?.trim() || null,
    };
}

/**
 * Resolve a friendly name for an `assets/:uuid` breadcrumb segment.
 *
 * This resolver did not exist, so `/assets/:uuid` rendered as `#c65f2671` —
 * a raw UUID where a serial number belongs. That is an AGENTS.md guardrail
 * violation ("no raw UUID shown to humans"), not polish: a CSR reading a
 * device page to a customer on the phone had nothing on screen naming the
 * device except the page body.
 *
 * Serial first, because that is the number stamped on the assembly and the
 * one the customer, the city and the tech all use. The device's own name
 * ("RP-1") is the fallback, and the truncated id remains only when a device
 * genuinely has neither.
 *
 * Mirrors the device page's query key ["asset", id] so it dedupes off a warm
 * cache and costs nothing when you arrive from that page.
 */
/**
 * Resolve a water provider's name for an `authorities/:uuid` crumb (#1426).
 * Same query key the provider page uses, so arriving from the list costs no
 * extra request. Never shows the raw id: falls back to the section label.
 */
function useAuthorityCrumbName(parts: string[]): { id: string; label: string } | null {
    const id = findEntityId(parts, "authorities");
    const { data } = useQuery({
        queryKey: ["water-provider", id],
        queryFn: () =>
            api.get<{ provider: { name: string; display_name: string | null } }>(`/api/water-providers/${id}`),
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id) return null;
    const label = data?.provider ? data.provider.display_name?.trim() || data.provider.name : "Water provider";
    return { id, label };
}

function useAssetCrumbName(parts: string[]): { id: string; label: string } | null {
    const id = findEntityId(parts, "assets");
    const { data } = useQuery({
        queryKey: ["asset", id],
        queryFn: () => api.get<{ asset: { serial_number: string | null; name: string | null } }>(`/api/assets/${id}`),
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id || !data?.asset) return null;
    return { id, label: assetCrumbLabel(data.asset, id) };
}

/**
 * Resolve a friendly name for a `jobs/:uuid` breadcrumb segment. Mirrors the
 * job-detail page's query key ["job", id] so it dedupes off a warm cache.
 */
function useJobCrumbName(parts: string[]): { id: string; label: string } | null {
    // On /field/backflow/:uuid etc. the job uuid follows the workflow segment,
    // not "jobs" — resolve it too so the trail can show the job title.
    const id = findEntityId(parts, "jobs") ?? findFieldJobId(parts);
    const { data } = useQuery({
        queryKey: ["job", id],
        queryFn: () => api.get<{ job: { title: string | null } }>(`/api/jobs/${id}`),
        enabled: !!id,
        staleTime: 60_000,
        retry: false,
    });
    if (!id || !data?.job) return null;
    const label = data.job.title?.trim() || `#${id.slice(0, 8)}`;
    return { id, label };
}

/**
 * Resolve a friendly label for a `commercial/:uuid` breadcrumb segment. The
 * commercial detail page distinguishes invoice vs estimate via the `?type=`
 * query param, so mirror that: invoices key off ["invoice", id] (number),
 * estimates off ["estimate", id] (display number).
 */
function useCommercialCrumbName(parts: string[], search: string): { id: string; label: string } | null {
    const id = findEntityId(parts, "commercial");
    const isInvoice = new URLSearchParams(search).get("type") === "invoice";
    const invoiceQ = useQuery({
        queryKey: ["invoice", id],
        queryFn: async () => {
            const r = await api.get<{
                invoice: { invoice_number: string | null };
                lines?: unknown[];
                payments?: unknown[];
            }>(`/api/invoices/${id}`);
            return { invoice: r.invoice, lines: r.lines ?? [], payments: r.payments ?? [] };
        },
        enabled: !!id && isInvoice,
        staleTime: 60_000,
        retry: false,
    });
    const estimateQ = useQuery({
        queryKey: ["estimate", id],
        queryFn: () => api.get<{ estimate: { display_number: string | null } }>(`/api/estimates/${id}`),
        enabled: !!id && !isInvoice,
        staleTime: 60_000,
        retry: false,
    });
    if (!id) return null;
    if (isInvoice) {
        const num = invoiceQ.data?.invoice?.invoice_number;
        return { id, label: num ? `Invoice #${num}` : `#${id.slice(0, 8)}` };
    }
    const num = estimateQ.data?.estimate?.display_number;
    return { id, label: num ? `Estimate #${num}` : `#${id.slice(0, 8)}` };
}

export function Breadcrumbs() {
    const location = useLocation();
    const parts = location.pathname.split("/").filter(Boolean);
    // The Quiet Toolbar (10/10): /notifications is "Recent activity" there.
    const [quiet] = useQuietToolbar();
    const userCrumb = useUserCrumbName(parts);
    const customerCrumb = useCustomerCrumbName(parts);
    const invoiceCrumb = useInvoiceCrumbName(parts);
    const paymentCrumb = usePaymentCrumbName(parts);
    const siteCrumb = useSiteCrumbName(parts);
    const jobCrumb = useJobCrumbName(parts);
    const assetCrumb = useAssetCrumbName(parts);
    const authorityCrumb = useAuthorityCrumbName(parts);
    const commercialCrumb = useCommercialCrumbName(parts, location.search);

    if (parts.length === 0) return null;

    const crumbs: { label: string; path: string }[] = [];
    const fieldJobId = findFieldJobId(parts);

    if (fieldJobId) {
        // Field workflow trail: `Field > <Job Title> [> <Workflow>]`. The bare
        // list paths (/field/backflow, /field/jobs, ...) have no page of their
        // own, so the job crumb links to the job detail instead of a blank list.
        const jobLabel = jobCrumb && jobCrumb.id === fieldJobId ? jobCrumb.label : `Job #${fieldJobId.slice(0, 8)}`;
        crumbs.push({ label: LABELS.field, path: "/field" });
        crumbs.push({ label: jobLabel, path: `/field/jobs/${fieldJobId}` });
        if (parts[1] !== "jobs") {
            crumbs.push({
                label: LABELS[parts[1]] ?? parts[1].charAt(0).toUpperCase() + parts[1].slice(1),
                path: location.pathname,
            });
        }
    } else {
        let accumulated = "";
        for (const part of parts) {
            accumulated += `/${part}`;
            // Location Page V2: a site lives under its customer, so the trail
            // reads Customers > {Customer} > {Site} instead of a flat
            // Locations > {Site}. Only swaps in when we're on a /sites/:uuid
            // path and the parent customer has resolved.
            if (part === "sites" && siteCrumb?.customerId && !parts.includes("customers")) {
                crumbs.push({ label: "Customers", path: "/customers" });
                crumbs.push({
                    label: siteCrumb.customerName ?? `#${siteCrumb.customerId.slice(0, 8)}`,
                    path: `/customers/${siteCrumb.customerId}`,
                });
                continue;
            }
            if (isUuid(part)) {
                let label = `#${part.slice(0, 8)}`;
                if (userCrumb && userCrumb.userId === part) label = userCrumb.name;
                else if (customerCrumb && customerCrumb.id === part) label = customerCrumb.label;
                else if (invoiceCrumb && invoiceCrumb.id === part) label = invoiceCrumb.label;
                else if (paymentCrumb && paymentCrumb.id === part) label = paymentCrumb.label;
                else if (siteCrumb && siteCrumb.id === part) label = siteCrumb.label;
                else if (jobCrumb && jobCrumb.id === part) label = jobCrumb.label;
                else if (assetCrumb && assetCrumb.id === part) label = assetCrumb.label;
                else if (authorityCrumb && authorityCrumb.id === part) label = authorityCrumb.label;
                else if (commercialCrumb && commercialCrumb.id === part) label = commercialCrumb.label;
                crumbs.push({ label, path: accumulated });
            } else {
                crumbs.push({
                    label:
                        quiet && part === "notifications"
                            ? "Recent activity"
                            : (LABELS[part] ?? part.charAt(0).toUpperCase() + part.slice(1)),
                    path: accumulated,
                });
            }
        }
    }

    return (
        <nav className="flex items-center gap-1.5 text-sm text-shell-foreground/70">
            <Link
                to="/"
                aria-label="Home"
                className="inline-flex items-center justify-center hover:text-shell-foreground transition-colors max-md:min-h-11 max-md:min-w-11"
            >
                <Home className="h-3.5 w-3.5" />
            </Link>
            {crumbs.map((crumb, i) => (
                <span key={crumb.path} className="flex items-center gap-1.5">
                    <ChevronRight className="h-3 w-3" />
                    {i === crumbs.length - 1 ? (
                        <span className="text-shell-foreground font-bold">{crumb.label}</span>
                    ) : (
                        <Link
                            to={crumb.path}
                            className="inline-flex items-center hover:text-shell-foreground transition-colors max-md:min-h-11"
                        >
                            {crumb.label}
                        </Link>
                    )}
                </span>
            ))}
        </nav>
    );
}
